import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Group,
  Text,
  Badge,
  Button,
  ActionIcon,
  Tooltip,
  Menu,
} from "@mantine/core";
import {
  IconEye,
  IconDownload,
  IconLock,
  IconLockOpen,
  IconEdit,
  IconCircleCheck,
  IconDots,
  IconPlus,
  IconAlertTriangle,
  IconMessage,
  IconPrinter,
  IconFlask,
  IconMicroscope,
  IconScissors,
  IconSettings,
  IconSend,
  IconHistory,
  IconSwitchHorizontal,
  IconFilePlus,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { CaseStatusBadge } from "../../../components/cases";
import { RecallReportButton } from "../../../components/report-delivery/RecallReportButton";
import { ReportDeliveryStateBadge } from "../../../components/report-delivery/ReportDeliveryStateBadge";
import type { RecallCountdownPhase } from "../../../hooks/useReportDeliveryRecall";
import type { RecallState } from "../../../services/reportDeliveryRecall.service";
import { getDisplayCaseNumber } from "../../../utils/caseIdentity";
import { PrinterPreferencesModal } from "./PrinterPreferencesModal";
import type { LegacyReportKind } from "../../../types/legacyCases";

interface Props {
  caseData: any;
  legacyCanIssueReportRevisions?: boolean;
  legacyHasNativeReport?: boolean;
  legacyReportKind?: LegacyReportKind;
  patientName: string;
  patientAge: string;
  mrn: string;
  currentStatusName: string;
  isLocked: boolean;
  isLockedByMe: boolean;
  isLockedByOther: boolean;
  lockInfo: any;
  lockLoading: boolean;
  lockOperationPending: boolean;
  canAccessFrontOffice: boolean;
  canAccessGrossing: boolean;
  canAccessEmbedding: boolean;
  canAccessCutting: boolean;
  canAccessPathologistReview: boolean;
  authProfileId: string | undefined;
  isPathologistReviewStatus: boolean;
  showGrossingButton: boolean;
  showEmbeddingButton: boolean;
  showCuttingButton: boolean;
  showTechnicalPrintActions: boolean;
  onPrintAllSlides: () => void;
  isLockedByOtherDisabled: boolean;
  onLockToggle: () => void;
  onEditCase: () => void;
  onSignOff: () => void;
  onContinueGrossing: () => void;
  onContinueEmbedding: () => void;
  onContinueCutting: () => void;
  onReportError: () => void;
  onAddNote: () => void;
  onOpenPreview: () => void;
  onDownloadReport: () => void;
  isDownloadingReport?: boolean;
  showTcTechnicalSummary?: boolean;
  onPreviewTcTechnicalSummary?: () => void;
  onDownloadTcTechnicalSummary?: () => void;
  isDownloadingTcTechnicalSummary?: boolean;
  onSendReport: () => void;
  isSendingReport?: boolean;
  scrollToTab: (tabValue: string) => void;
  reportRecallState?: RecallState;
  reportRecallRemainingSeconds: number | null;
  reportRecallCountdownPhase: RecallCountdownPhase;
  onRecallReport: () => void;
  onOpenReportDeliveryHistory: () => void;
  canAssignPathologist?: boolean;
  onAssignPathologist?: () => void;
  canReplacePathologist?: boolean;
  onReplacePathologist?: () => void;
  canReassignPrefix?: boolean;
  onReassignPrefix?: () => void;
  canRequestAdditionalStains?: boolean;
  onRequestAdditionalStains?: () => void;
  canInitiateTissueCypherAddendum?: boolean;
  onInitiateTissueCypherAddendum?: () => void;
  showTcExternalCompletion?: boolean;
  tcExternalCompletionEnabled?: boolean;
  tcExternalCompletionLoading?: boolean;
  onTcExternalCompletion?: () => void;
  showAdjacentNavigation?: boolean;
  previousCaseDisabled?: boolean;
  nextCaseDisabled?: boolean;
  previousCaseLoading?: boolean;
  nextCaseLoading?: boolean;
  onPreviousCase?: () => void;
  onNextCase?: () => void;
}

export default function CaseDetailHeaderV2({
  caseData,
  legacyCanIssueReportRevisions = false,
  legacyHasNativeReport = false,
  legacyReportKind,
  patientName,
  patientAge,
  mrn,
  currentStatusName,
  isLocked,
  isLockedByMe,
  isLockedByOther,
  lockInfo,
  lockLoading,
  lockOperationPending,
  canAccessFrontOffice,
  canAccessGrossing,
  canAccessEmbedding,
  canAccessCutting,
  canAccessPathologistReview,
  authProfileId,
  isPathologistReviewStatus,
  showGrossingButton,
  showEmbeddingButton,
  showCuttingButton,
  showTechnicalPrintActions,
  onPrintAllSlides,
  isLockedByOtherDisabled,
  onLockToggle,
  onEditCase,
  onSignOff,
  onContinueGrossing,
  onContinueEmbedding,
  onContinueCutting,
  onReportError,
  onAddNote,
  onOpenPreview,
  onDownloadReport,
  isDownloadingReport,
  showTcTechnicalSummary = false,
  onPreviewTcTechnicalSummary,
  onDownloadTcTechnicalSummary,
  isDownloadingTcTechnicalSummary = false,
  onSendReport,
  isSendingReport,
  scrollToTab,
  reportRecallState,
  reportRecallRemainingSeconds,
  reportRecallCountdownPhase,
  onRecallReport,
  onOpenReportDeliveryHistory,
  canAssignPathologist = false,
  onAssignPathologist,
  canReplacePathologist = false,
  onReplacePathologist,
  canReassignPrefix = false,
  onReassignPrefix,
  canRequestAdditionalStains = false,
  onRequestAdditionalStains,
  canInitiateTissueCypherAddendum = false,
  onInitiateTissueCypherAddendum,
  showTcExternalCompletion = false,
  tcExternalCompletionEnabled = false,
  tcExternalCompletionLoading = false,
  onTcExternalCompletion,
  showAdjacentNavigation = false,
  previousCaseDisabled = true,
  nextCaseDisabled = true,
  previousCaseLoading = false,
  nextCaseLoading = false,
  onPreviousCase,
  onNextCase,
}: Props) {
  const [printerModalOpen, setPrinterModalOpen] = useState(false);
  const isLegacyImported = useMemo(
    () => caseData?.isLegacyImported === true,
    [caseData?.isLegacyImported]
  );
  const isLegacyTechnicalReport = useMemo(
    () => isLegacyImported && legacyReportKind === "technical",
    [isLegacyImported, legacyReportKind]
  );
  const technicalPreviewLabel = useMemo(
    () =>
      isLegacyTechnicalReport
        ? "View Original Technical Summary"
        : "Preview Technical Summary",
    [isLegacyTechnicalReport]
  );
  const technicalDownloadLabel = useMemo(
    () =>
      isLegacyTechnicalReport
        ? "Download Original Technical Summary"
        : "Download Technical Summary",
    [isLegacyTechnicalReport]
  );

  const handleOpenPrinterModal = useCallback(() => {
    setPrinterModalOpen(true);
  }, []);
  const handleClosePrinterModal = useCallback(() => {
    setPrinterModalOpen(false);
  }, []);
  const handleAddComment = useCallback(() => {
    scrollToTab("comments");
  }, [scrollToTab]);

  // Sign-off: only show if the assigned pathologist is the viewing user
  const isAssignedPathologist =
    canAccessPathologistReview && authProfileId === caseData?.pathologistId;
  const isTcCase = caseData?.accessioningType === "tc_only";
  const showSignOff =
    !isLegacyTechnicalReport &&
    (!isLegacyImported || legacyCanIssueReportRevisions) &&
    isAssignedPathologist &&
    currentStatusName !== "Draft" &&
    !isTcCase;
  const signOffLabel =
    caseData?.signedOffAt || (isLegacyImported && legacyCanIssueReportRevisions)
      ? "Update Report"
      : "Sign Off";

  const isSignedOff = caseData?.isFinalized === true;
  const showDownload =
    !isLegacyTechnicalReport &&
    (!isLegacyImported || legacyHasNativeReport) &&
    (isSignedOff || Boolean(caseData?.signedOffAt));
  const showPreview =
    !isLegacyTechnicalReport &&
    (!isLegacyImported || legacyHasNativeReport) &&
    (currentStatusName === "Pathologist Review" ||
      currentStatusName === "Signed Off" ||
      showDownload);
  const previewLabel = showDownload ? "View Report" : "Preview";
  const showSendReport =
    !isLegacyTechnicalReport &&
    (!isLegacyImported || legacyHasNativeReport) &&
    isSignedOff;
  const headerReportDeliveryState = useMemo(() => {
    if (isLegacyTechnicalReport) return null;
    const state = reportRecallState?.aggregateState;
    return state === "recalled" || state === "delivered" ? state : null;
  }, [isLegacyTechnicalReport, reportRecallState?.aggregateState]);
  const isCurrentReportFullyRecalled = useMemo(
    () => headerReportDeliveryState === "recalled",
    [headerReportDeliveryState]
  );

  const showLock =
    canAccessFrontOffice &&
    !isPathologistReviewStatus &&
    currentStatusName !== "Signed Off";

  const lockTooltip = lockLoading
    ? "Checking lock status..."
    : isLockedByMe
    ? `Locked by you${
        lockInfo?.lockReason ? ` — ${lockInfo.lockReason}` : ""
      } — click to unlock`
    : isLockedByOther
    ? `Locked by ${lockInfo?.lockedBy?.fullName || "another user"} until ${
        lockInfo?.expiresAt
          ? dayjs(lockInfo.expiresAt).format("h:mm A")
          : "unknown"
      }${lockInfo?.lockReason ? ` — ${lockInfo.lockReason}` : ""}`
    : "Click to lock case";

  // Workflow button label based on status
  const grossingLabel =
    currentStatusName === "Grossing" ? "Continue Grossing" : "Start Grossing";
  const embeddingLabel =
    currentStatusName === "Embedding"
      ? "Continue Embedding"
      : "Start Embedding";
  const cuttingLabel =
    currentStatusName === "Cutting" ? "Continue Cutting" : "Start Cutting";

  // Determine single primary CTA: Sign-off > Workflow > View/Preview
  let primaryButton: React.ReactNode = null;
  if (showSignOff) {
    primaryButton = (
      <Button
        size="sm"
        fw={700}
        leftSection={<IconCircleCheck size={16} />}
        style={{ boxShadow: "var(--mantine-shadow-xs)" }}
        onClick={onSignOff}
        disabled={isLockedByOtherDisabled}
      >
        {signOffLabel}
      </Button>
    );
  } else if (showGrossingButton && canAccessGrossing) {
    primaryButton = (
      <Button
        variant="filled"
        color="blue"
        size="sm"
        fw={700}
        leftSection={<IconFlask size={16} />}
        onClick={onContinueGrossing}
        disabled={isLockedByOtherDisabled}
      >
        {grossingLabel}
      </Button>
    );
  } else if (showEmbeddingButton && canAccessEmbedding) {
    primaryButton = (
      <Button
        variant="filled"
        color="teal"
        size="sm"
        fw={700}
        leftSection={<IconMicroscope size={16} />}
        onClick={onContinueEmbedding}
        disabled={isLockedByOtherDisabled}
      >
        {embeddingLabel}
      </Button>
    );
  } else if (showCuttingButton && canAccessCutting) {
    primaryButton = (
      <Button
        variant="light"
        color="blue"
        size="sm"
        fw={700}
        leftSection={<IconScissors size={16} />}
        onClick={onContinueCutting}
        disabled={isLockedByOtherDisabled}
      >
        {cuttingLabel}
      </Button>
    );
  } else if (
    isLegacyTechnicalReport &&
    showTcTechnicalSummary &&
    onPreviewTcTechnicalSummary
  ) {
    primaryButton = (
      <Button
        variant="default"
        size="sm"
        leftSection={<IconEye size={16} />}
        onClick={onPreviewTcTechnicalSummary}
      >
        {technicalPreviewLabel}
      </Button>
    );
  } else if (showPreview) {
    primaryButton = (
      <Button
        variant="default"
        size="sm"
        leftSection={<IconEye size={16} />}
        onClick={onOpenPreview}
      >
        {previewLabel}
      </Button>
    );
  }

  return (
    <>
      <Box
        style={{
          position: "sticky",
          top: "var(--app-shell-header-height, 60px)",
          zIndex: 100,
          background: "var(--mantine-color-body)",
          borderBottom: "1px solid var(--mantine-color-gray-2)",
          boxShadow: "var(--mantine-shadow-xs)",
        }}
        px="lg"
        py="sm"
      >
        <Group
          justify="space-between"
          gap="md"
          maw={1600}
          mx="auto"
          wrap="wrap"
        >
          <Group gap="md" wrap="nowrap">
            {showAdjacentNavigation && onPreviousCase && (
              <Tooltip label="Previous case">
                <ActionIcon
                  variant="light"
                  size="lg"
                  aria-label="Previous case"
                  onClick={onPreviousCase}
                  loading={previousCaseLoading}
                  disabled={previousCaseDisabled}
                >
                  <IconChevronLeft size={18} />
                </ActionIcon>
              </Tooltip>
            )}
            <Box
              style={{
                background: "var(--mantine-color-blue-0)",
                borderRadius: "var(--mantine-radius-md)",
                padding: 8,
                display: "flex",
                alignItems: "center",
              }}
            >
              <IconMicroscope size={20} color="var(--mantine-color-blue-7)" />
            </Box>
            <Box>
              <Group gap="sm" wrap="nowrap">
                <Text fw={700} size="xl" style={{ letterSpacing: "-0.01em" }}>
                  {patientName}
                  {patientAge ? ` (${patientAge})` : ""}
                </Text>
                {caseData?.isRush && (
                  <Tooltip
                    label={
                      caseData.rushReason || "Rush case — expedited processing"
                    }
                  >
                    <Badge
                      color="red"
                      variant="filled"
                      size="sm"
                      tt="uppercase"
                      style={{ borderRadius: 9999 }}
                    >
                      RUSH
                    </Badge>
                  </Tooltip>
                )}
                {caseData?.isHighPriority && (
                  <Tooltip
                    label={
                      caseData.highPriorityReason ||
                      "High priority — flagged for attention"
                    }
                  >
                    <Badge
                      color="red"
                      variant="light"
                      size="sm"
                      tt="uppercase"
                      style={{
                        borderRadius: 9999,
                        border: "1px solid var(--mantine-color-red-2)",
                      }}
                    >
                      High Priority
                    </Badge>
                  </Tooltip>
                )}
              </Group>
              <Group gap="md" mt={2}>
                {mrn && (
                  <Text size="sm" c="dimmed" fw={500}>
                    MRN: {mrn}
                  </Text>
                )}
                <CaseStatusBadge
                  statusName={currentStatusName}
                  isBlocked={caseData?.isBlocked}
                />
                {headerReportDeliveryState && (
                  <ReportDeliveryStateBadge state={headerReportDeliveryState} />
                )}
                <Text size="sm" c="blue" fw={700}>
                  #{getDisplayCaseNumber(caseData)}
                </Text>
                {caseData?.externalCaseNumber && (
                  <Tooltip label="External ID / Referring Lab Case #">
                    <Badge
                      color="grape"
                      variant="light"
                      size="sm"
                      style={{
                        borderRadius: 9999,
                        border: "1px solid var(--mantine-color-grape-3)",
                      }}
                    >
                      Ext: {caseData.externalCaseNumber}
                    </Badge>
                  </Tooltip>
                )}
              </Group>
            </Box>
          </Group>

          <Group gap={8} wrap="wrap">
            {/* Lock icon */}
            {showLock && (
              <Tooltip label={lockTooltip}>
                <ActionIcon
                  variant="light"
                  size="lg"
                  color={
                    isLockedByMe ? "blue" : isLockedByOther ? "red" : "green"
                  }
                  onClick={onLockToggle}
                  loading={lockOperationPending}
                  disabled={lockLoading}
                >
                  {isLocked ? (
                    <IconLock size={18} />
                  ) : (
                    <IconLockOpen size={18} />
                  )}
                </ActionIcon>
              </Tooltip>
            )}

            {/* Single primary CTA */}
            {primaryButton}

            {!isLegacyTechnicalReport && reportRecallState?.canRecall && (
              <RecallReportButton
                remainingSeconds={reportRecallRemainingSeconds}
                countdownPhase={reportRecallCountdownPhase}
                methods={reportRecallState.methods}
                scheduledAt={reportRecallState.earliestPendingScheduledAt}
                recipientCount={reportRecallState.recipientCount}
                onClick={onRecallReport}
              />
            )}

            {!isLegacyTechnicalReport &&
              !reportRecallState?.canRecall &&
              reportRecallState &&
              !headerReportDeliveryState &&
              reportRecallState.aggregateState !== "none" && (
                <ReportDeliveryStateBadge
                  state={reportRecallState.aggregateState}
                  onClick={onOpenReportDeliveryHistory}
                  actionLabel="View report delivery history"
                />
              )}

            {/* Actions Menu */}
            <Menu
              shadow="md"
              width={220}
              position="bottom-end"
              offset={8}
              withinPortal
            >
              <Menu.Target>
                <ActionIcon
                  variant="default"
                  size="lg"
                  aria-label="Case actions"
                >
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {showPreview && (
                  <Menu.Item
                    leftSection={<IconEye size={14} />}
                    onClick={onOpenPreview}
                  >
                    {previewLabel}
                  </Menu.Item>
                )}
                {showDownload && (
                  <Menu.Item
                    leftSection={<IconDownload size={14} />}
                    onClick={onDownloadReport}
                    disabled={isDownloadingReport}
                  >
                    Download Report
                  </Menu.Item>
                )}
                {showTcTechnicalSummary && onPreviewTcTechnicalSummary && (
                  <Menu.Item
                    leftSection={<IconEye size={14} />}
                    onClick={onPreviewTcTechnicalSummary}
                  >
                    {technicalPreviewLabel}
                  </Menu.Item>
                )}
                {showTcTechnicalSummary && onDownloadTcTechnicalSummary && (
                  <Menu.Item
                    leftSection={<IconDownload size={14} />}
                    onClick={onDownloadTcTechnicalSummary}
                    disabled={isDownloadingTcTechnicalSummary}
                  >
                    {technicalDownloadLabel}
                  </Menu.Item>
                )}
                {showSendReport && (
                  <Menu.Item
                    leftSection={<IconSend size={14} />}
                    onClick={onSendReport}
                    disabled={isSendingReport}
                    color={isCurrentReportFullyRecalled ? "orange" : undefined}
                  >
                    Send Report
                  </Menu.Item>
                )}
                {headerReportDeliveryState && (
                  <Menu.Item
                    leftSection={<IconHistory size={14} />}
                    onClick={onOpenReportDeliveryHistory}
                  >
                    View Delivery History
                  </Menu.Item>
                )}
                {(showPreview || showDownload || showTcTechnicalSummary) && (
                  <Menu.Divider />
                )}
                {!isLegacyImported && canAccessFrontOffice && (
                  <Menu.Item
                    leftSection={<IconEdit size={14} />}
                    onClick={onEditCase}
                    disabled={isLockedByOtherDisabled}
                  >
                    Edit Case
                  </Menu.Item>
                )}
                {canReplacePathologist && onReplacePathologist && (
                  <Menu.Item
                    leftSection={<IconSwitchHorizontal size={14} />}
                    onClick={onReplacePathologist}
                  >
                    Replace pathologist
                  </Menu.Item>
                )}
                {canAssignPathologist && onAssignPathologist && (
                  <Menu.Item
                    leftSection={<IconSwitchHorizontal size={14} />}
                    onClick={onAssignPathologist}
                  >
                    Assign pathologist
                  </Menu.Item>
                )}
                {canReassignPrefix && onReassignPrefix && (
                  <Menu.Item
                    leftSection={<IconSwitchHorizontal size={14} />}
                    onClick={onReassignPrefix}
                  >
                    Change case prefix
                  </Menu.Item>
                )}
                {canRequestAdditionalStains && onRequestAdditionalStains && (
                  <Menu.Item
                    leftSection={<IconFlask size={14} />}
                    onClick={onRequestAdditionalStains}
                  >
                    Request Additional Stains
                  </Menu.Item>
                )}
                {!isLegacyImported &&
                  canInitiateTissueCypherAddendum &&
                  onInitiateTissueCypherAddendum && (
                    <Menu.Item
                      leftSection={<IconFilePlus size={14} />}
                      onClick={onInitiateTissueCypherAddendum}
                    >
                      Add Tissue Cypher Addendum
                    </Menu.Item>
                  )}
                {showTcExternalCompletion && onTcExternalCompletion && (
                  <Menu.Item
                    leftSection={<IconCircleCheck size={14} />}
                    onClick={onTcExternalCompletion}
                    disabled={
                      !tcExternalCompletionEnabled ||
                      tcExternalCompletionLoading
                    }
                  >
                    Mark external action completed
                  </Menu.Item>
                )}
                {showTechnicalPrintActions && (
                  <>
                    <Menu.Item
                      leftSection={<IconPrinter size={14} />}
                      onClick={handleOpenPrinterModal}
                    >
                      Print All Cassettes
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconPrinter size={14} />}
                      onClick={onPrintAllSlides}
                    >
                      Print All Slides
                    </Menu.Item>
                  </>
                )}
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconAlertTriangle size={14} />}
                  color="red"
                  onClick={onReportError}
                >
                  Report Error
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconPlus size={14} />}
                  onClick={onAddNote}
                >
                  Add Note
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconMessage size={14} />}
                  onClick={handleAddComment}
                >
                  Add Comment
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconSettings size={14} />}
                  onClick={handleOpenPrinterModal}
                >
                  Printer Preferences
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            {showAdjacentNavigation && onNextCase && (
              <Tooltip label="Next case">
                <ActionIcon
                  variant="light"
                  size="lg"
                  aria-label="Next case"
                  onClick={onNextCase}
                  loading={nextCaseLoading}
                  disabled={nextCaseDisabled}
                >
                  <IconChevronRight size={18} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Group>
      </Box>

      <PrinterPreferencesModal
        opened={printerModalOpen}
        onClose={handleClosePrinterModal}
      />
    </>
  );
}
